import { useCallback, useState } from "react";
import AdminRoutes from "./admin/AdminRoutes";
import AppErrorBoundary from "./components/public/AppErrorBoundary";
import LocationSelector from "./components/public/LocationSelector";
import PublicFooter from "./components/public/PublicFooter";
import PublicHeader from "./components/public/PublicHeader";
import { LocationProvider } from "./context/LocationContext";
import AboutPage from "./pages/About/AboutPage";
import ContactPage from "./pages/Contact/ContactPage";
import HomePage from "./pages/Home/HomePage";
import MenuPage from "./pages/Menu/MenuPage";
import CartPage from "./pages/Cart/CartPage";
import CheckoutPage from "./pages/Checkout/CheckoutPage";
import OrderConfirmationPage from "./pages/OrderConfirmation/OrderConfirmationPage";
import SimplePage from "./pages/SimplePages/SimplePage";
import "./App.css";

function getCurrentPath() {
  return window.location.pathname || "/";
}

function getLocationSlug(pathname) {
  const parts = pathname.split("/").filter(Boolean);

  if (parts[0] !== "menu") {
    return "";
  }

  return parts[1] || "";
}

function PublicRoutes() {
  const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);
  const currentPath = getCurrentPath();

  const openLocationSelector = useCallback(() => {
    setLocationSelectorOpen(true);
  }, []);

  const closeLocationSelector = useCallback(() => {
    setLocationSelectorOpen(false);
  }, []);

  function handleLocationSelected(location) {
    setLocationSelectorOpen(false);
    window.location.href = `/menu/${location.slug}`;
  }

  function renderPublicPage() {
    if (currentPath === "/" || currentPath === "") {
      return <HomePage onChooseLocation={openLocationSelector} />;
    }

    if (currentPath === "/menu" || currentPath.startsWith("/menu/")) {
      return <MenuPage locationSlug={getLocationSlug(currentPath)} onChooseLocation={openLocationSelector} />;
    }

    if (currentPath === "/catering") {
      return (
        <SimplePage
          action={<a className="btn btn-primary" href="/contact">Contact Zayas Kitchen</a>}
          eyebrow="Catering"
          title="Catering requests are coming next."
        >
          Tell us about your gathering and preferred location. The full public catering form will connect to
          the existing catering request backend in the next pass.
        </SimplePage>
      );
    }

    if (currentPath === "/about") {
      return <AboutPage onChooseLocation={openLocationSelector} />;
    }

    if (currentPath === "/contact") {
      return <ContactPage />;
    }

    if (currentPath === "/cart") {
      return <CartPage onChooseLocation={openLocationSelector} />;
    }

    if (currentPath === "/checkout") {
      return <CheckoutPage onChooseLocation={openLocationSelector} />;
    }

    if (currentPath.startsWith("/order/") && currentPath.endsWith("/confirmation")) {
      return <OrderConfirmationPage />;
    }

    return (
      <SimplePage eyebrow="404" title="That page could not be found.">
        Head back home or choose a location to browse the menu.
      </SimplePage>
    );
  }

  return (
    <LocationProvider>
      <PublicHeader currentPath={currentPath} onOpenLocationSelector={openLocationSelector} />
      {renderPublicPage()}
      <PublicFooter />
      <LocationSelector
        onClose={closeLocationSelector}
        onLocationSelected={handleLocationSelected}
        open={locationSelectorOpen}
      />
    </LocationProvider>
  );
}

function App() {
  if (window.location.pathname.startsWith("/admin")) {
    return (
      <AppErrorBoundary homeHref="/admin" homeLabel="Back to Dashboard">
        <AdminRoutes />
      </AppErrorBoundary>
    );
  }

  return (
    <AppErrorBoundary>
      <PublicRoutes />
    </AppErrorBoundary>
  );
}

export default App;